import type { PaymentBillingDetailsModel } from '../models/interfaces/payment-billing-details.interface';

/**
 * PD-17281 — the longest value each billing field may carry. These are
 * CyberSource's own `orderInformation.billTo` limits (the strictest of the
 * gateways that receive them), so a value that passes here is never refused
 * by the processor for its length. The name is split into first and last
 * name by the adapter, each of which allows 60.
 */
const BILLING_FIELD_MAX_LENGTH: Readonly<
	Record<keyof PaymentBillingDetailsModel, number>
> = {
	name: 120,
	address1: 60,
	address2: 60,
	city: 50,
	state: 20,
	zip: 10,
	country: 2,
	email: 255,
};

const BILLING_FIELDS = Object.keys(BILLING_FIELD_MAX_LENGTH) as Array<
	keyof PaymentBillingDetailsModel
>;

/**
 * PD-17281 — billing details for a one-time card as they arrive in a submit
 * body, reduced to the known string fields, trimmed and cut to the
 * processor's limits. Anything that is not a plain object, and any field
 * that is not a non-empty string, is dropped rather than refused: the
 * details are only ever extra context for the gateway, and a malformed one
 * must not stop an order. Null when nothing usable remains.
 *
 * The storefront submit body is a plain interface that no validation pipe
 * inspects, so this is the only check its values get before reaching a
 * gateway request.
 */
export function normalizePaymentBillingDetails(
	input: unknown,
): PaymentBillingDetailsModel | null {
	if (input == null || typeof input !== 'object' || Array.isArray(input)) {
		return null;
	}

	const source = input as Record<string, unknown>;
	const result: PaymentBillingDetailsModel = {};
	let hasValue = false;

	for (const field of BILLING_FIELDS) {
		// Own properties only, so a key inherited from the prototype chain is
		// never read as billing data.
		if (!Object.prototype.hasOwnProperty.call(source, field)) {
			continue;
		}

		const raw = source[field];
		if (typeof raw !== 'string') {
			continue;
		}

		const trimmed = raw.trim();
		if (trimmed === '') {
			continue;
		}

		result[field] = trimmed.slice(0, BILLING_FIELD_MAX_LENGTH[field]);
		hasValue = true;
	}

	return hasValue ? result : null;
}
